import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Building2,
  Plus,
  ChevronDown,
  ChevronRight,
  Loader2,
  AlertCircle,
  Edit2,
  Trash2,
  Search,
} from 'lucide-react';
import api, { extractError } from '../lib/api';
import type { Organization, PaginatedResponse } from '../types';
import { OrganizationLevel } from '../types';

const levelLabels: Record<OrganizationLevel, string> = {
  [OrganizationLevel.Generalsekretariat]: 'Generalsekretariat',
  [OrganizationLevel.Landesverband]: 'Landesverband',
  [OrganizationLevel.Bereich]: 'Bereich',
  [OrganizationLevel.Bezirksstelle]: 'Bezirksstelle',
};

const levelColors: Record<OrganizationLevel, string> = {
  [OrganizationLevel.Generalsekretariat]: 'bg-red-700 text-white',
  [OrganizationLevel.Landesverband]: 'bg-accent-blue text-white',
  [OrganizationLevel.Bereich]: 'bg-accent-turquoise-dark text-white',
  [OrganizationLevel.Bezirksstelle]: 'bg-accent-green-dark text-white',
};

function buildTree(orgs: Organization[]): Organization[] {
  const map = new Map<string, Organization>();
  const roots: Organization[] = [];

  for (const org of orgs) {
    map.set(org.id, { ...org, children: [] });
  }

  for (const org of orgs) {
    const node = map.get(org.id)!;
    if (org.parentId && map.has(org.parentId)) {
      map.get(org.parentId)!.children!.push(node);
    } else {
      roots.push(node);
    }
  }

  return roots;
}

function sortTree(nodes: Organization[]): void {
  nodes.sort((a, b) => a.name.localeCompare(b.name));
  for (const node of nodes) {
    if (node.children && node.children.length > 0) {
      sortTree(node.children);
    }
  }
}

function TreeNode({
  node,
  onEdit,
  onDelete,
  depth = 0,
}: {
  node: Organization;
  onEdit: (org: Organization) => void;
  onDelete: (org: Organization) => void;
  depth?: number;
}) {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = node.children && node.children.length > 0;

  return (
    <div>
      <div
        className="flex items-center gap-2 rounded-lg px-3 py-2.5 hover:bg-gray-50 transition-colors group"
        style={{ paddingLeft: `${depth * 24 + 12}px` }}
      >
        {hasChildren ? (
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-gray-400 hover:text-gray-600"
            aria-label={expanded ? 'Einklappen' : 'Ausklappen'}
          >
            {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
        ) : (
          <div className="w-4" />
        )}

        <Building2 size={18} className="text-gray-400 shrink-0" />

        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{node.name}</p>
          <p className="text-xs text-gray-500">{node.shortName || node.level}</p>
        </div>

        <span className={`badge text-xs shrink-0 ${levelColors[node.level]}`}>
          {levelLabels[node.level]}
        </span>

        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
          <button
            onClick={() => onEdit(node)}
            className="btn-ghost p-1.5"
            title="Bearbeiten"
            aria-label={`${node.name} bearbeiten`}
          >
            <Edit2 size={14} />
          </button>
          <button
            onClick={() => onDelete(node)}
            className="btn-ghost p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50"
            title="Löschen"
            aria-label={`${node.name} löschen`}
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {expanded && hasChildren && (
        <div>
          {node.children!.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              onEdit={onEdit}
              onDelete={onDelete}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface OrgFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  editOrg?: Organization | null;
}

function OrgFormModal({ isOpen, onClose, editOrg }: OrgFormModalProps) {
  const queryClient = useQueryClient();
  const prevEditOrgRef = useRef(editOrg);
  const [formData, setFormData] = useState({
    name: editOrg?.name ?? '',
    shortName: editOrg?.shortName ?? '',
    level: editOrg?.level ?? OrganizationLevel.Bezirksstelle,
    parentId: editOrg?.parentId ?? '',
    metadata: editOrg?.metadata ?? {},
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (editOrg !== prevEditOrgRef.current) {
      setFormData({
        name: editOrg?.name ?? '',
        shortName: editOrg?.shortName ?? '',
        level: editOrg?.level ?? OrganizationLevel.Bezirksstelle,
        parentId: editOrg?.parentId ?? '',
        metadata: editOrg?.metadata ?? {},
      });
      setError(null);
      prevEditOrgRef.current = editOrg;
    }
  }, [editOrg]);

  const { data: orgsData } = useQuery<PaginatedResponse<Organization>>({
    queryKey: ['organizations'],
    queryFn: async () => {
      const res = await api.get('/organizations', { params: { limit: 100 } });
      const items = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
      return { data: items, total: items.length, page: 1, limit: 100, totalPages: 1 };
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const payload = {
        ...data,
        parentId: data.parentId || undefined,
      };
      const res = await api.post('/organizations', payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      onClose();
    },
    onError: (err) => setError(extractError(err).message),
  });

  const updateMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const payload = {
        ...data,
        parentId: data.parentId || undefined,
      };
      const res = await api.put(`/organizations/${editOrg!.id}`, payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      onClose();
    },
    onError: (err) => setError(extractError(err).message),
  });

  if (!isOpen) return null;

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (editOrg) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-xl bg-white shadow-xl">
        <div className="border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">
            {editOrg ? 'Organisation bearbeiten' : 'Neue Organisation'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
              <AlertCircle size={18} className="shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div>
            <label htmlFor="org-name" className="label">Name</label>
            <input
              id="org-name"
              type="text"
              className="input-field"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </div>

          <div>
            <label htmlFor="org-shortname" className="label">Kurzname (optional)</label>
            <input
              id="org-shortname"
              type="text"
              className="input-field"
              value={formData.shortName}
              onChange={(e) => setFormData({ ...formData, shortName: e.target.value })}
            />
          </div>

          <div>
            <label htmlFor="org-level" className="label">Ebene</label>
            <select
              id="org-level"
              className="input-field"
              value={formData.level}
              onChange={(e) => setFormData({ ...formData, level: e.target.value as OrganizationLevel })}
            >
              {Object.entries(levelLabels).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="org-parent" className="label">Übergeordnete Organisation</label>
            <select
              id="org-parent"
              className="input-field"
              value={formData.parentId}
              onChange={(e) => setFormData({ ...formData, parentId: e.target.value })}
            >
              <option value="">Keine (Root-Ebene)</option>
              {orgsData?.data
                .filter((o) => o.id !== editOrg?.id)
                .map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name} ({levelLabels[org.level]})
                  </option>
                ))}
            </select>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary">
              Abbrechen
            </button>
            <button type="submit" disabled={isSubmitting} className="btn-primary">
              {isSubmitting ? (
                <><Loader2 size={16} className="animate-spin" /> Speichern...</>
              ) : (
                editOrg ? 'Aktualisieren' : 'Erstellen'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function OrganizationsPage() {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editOrg, setEditOrg] = useState<Organization | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const { data, isLoading, isFetching, error } = useQuery<PaginatedResponse<Organization>>({
    queryKey: ['organizations'],
    queryFn: async () => {
      const res = await api.get('/organizations', { params: { limit: 100 } });
      const items = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
      return { data: items, total: items.length, page: 1, limit: 100, totalPages: 1 };
    },
    // Verhindert Race-Condition beim Seitenwechsel:
    // Cache bleibt 30s frisch, kein sofortiger Background-Refetch beim Navigieren
    staleTime: 30_000,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/organizations/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
  });

  const filteredOrgs = (data?.data ?? []).filter((org) =>
    org.name.toLowerCase().includes(searchQuery.toLowerCase()),
  ) ?? [];

  const rawTree = buildTree(filteredOrgs);
  sortTree(rawTree);
  const tree = rawTree;

  const handleEdit = (org: Organization) => {
    setEditOrg(org);
    setShowModal(true);
  };

  const handleDelete = (org: Organization) => {
    if (window.confirm(`Organisation "${org.name}" wirklich löschen?`)) {
      deleteMutation.mutate(org.id);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditOrg(null);
  };

  const loading = isLoading || (isFetching && tree.length === 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Building2 size={28} className="text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Organisationen</h1>
            <p className="text-sm text-gray-500">Hierarchische Organisationsstruktur</p>
          </div>
        </div>
        <button
          onClick={() => {
            setEditOrg(null);
            setShowModal(true);
          }}
          className="btn-primary"
        >
          <Plus size={18} />
          Neue Organisation
        </button>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Organisation suchen..."
          className="input-field pl-10"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          <AlertCircle size={18} className="shrink-0" />
          <span>Organisationen konnten nicht geladen werden</span>
        </div>
      )}

      {/* Tree */}
      <div className="card">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={24} className="animate-spin text-gray-400" />
          </div>
        ) : tree.length === 0 ? (
          <div className="py-12 text-center">
            <Building2 size={48} className="mx-auto text-gray-300 mb-3" />
            <p className="text-sm text-gray-500">
              {searchQuery ? 'Keine Organisationen gefunden' : 'Noch keine Organisationen vorhanden'}
            </p>
            {!searchQuery && (
              <button onClick={() => setShowModal(true)} className="btn-primary mt-4">
                <Plus size={18} /> Erste Organisation anlegen
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {tree.map((node) => (
              <TreeNode
                key={node.id}
                node={node}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      <OrgFormModal
        isOpen={showModal}
        onClose={handleCloseModal}
        editOrg={editOrg}
      />
    </div>
  );
}
